import { useCallback, useEffect, useRef } from "react";

import { resolveCurriculumVoiceTrackId } from "@/audio/curriculum-catalog";

import { useAudio } from "./useAudio";

export interface CurriculumAudioPlayer {
  readonly activeVoiceId: string | null;
  readonly isPlayingPath: (path: string) => boolean;
  readonly playPath: (path: string) => Promise<boolean>;
  readonly stopOwnedVoice: () => void;
}

export function useCurriculumAudioPlayer(
  ownerKey: number | string
): CurriculumAudioPlayer {
  const { commands, snapshot } = useAudio();
  const requestGenerationRef = useRef(0);
  const ownedPlaybackIdRef = useRef<number | null>(null);

  const stopOwnedVoice = useCallback(() => {
    requestGenerationRef.current += 1;
    const playbackId = ownedPlaybackIdRef.current;
    ownedPlaybackIdRef.current = null;

    if (playbackId !== null) {
      commands.stopVoice(playbackId);
    }
  }, [commands]);

  useEffect(() => {
    return stopOwnedVoice;
  }, [ownerKey, stopOwnedVoice]);

  const playPath = useCallback(
    async (path: string): Promise<boolean> => {
      const trackId = resolveCurriculumVoiceTrackId(path);
      if (trackId === null) {
        return false;
      }

      const requestGeneration = requestGenerationRef.current + 1;
      requestGenerationRef.current = requestGeneration;

      try {
        const unlock = await commands.unlock();
        if (
          unlock.status !== "unlocked" ||
          requestGenerationRef.current !== requestGeneration
        ) {
          return false;
        }

        const result = await commands.playVoice(trackId);
        if (requestGenerationRef.current !== requestGeneration) {
          if (
            result.status === "playing" &&
            result.playbackId !== undefined
          ) {
            commands.stopVoice(result.playbackId);
          }
          return false;
        }

        if (result.status !== "playing") {
          return false;
        }

        ownedPlaybackIdRef.current = result.playbackId ?? null;
        return true;
      } catch {
        return false;
      }
    },
    [commands]
  );

  const isPlayingPath = useCallback(
    (path: string) => snapshot.activeVoiceId === path,
    [snapshot.activeVoiceId]
  );

  return {
    activeVoiceId: snapshot.activeVoiceId,
    isPlayingPath,
    playPath,
    stopOwnedVoice
  };
}
